import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setEditId, selectUserById, setBanId } from "../../../store/mine_craft/usersSlice";
import { destroyUser, fetchUser } from "../../../actions/mine_craft/usersActions";
import { formattedDate } from "../../../helpers/DateHelpers";
import minecraftUrls from "../../../configs/mine_craft/minecraftUrls";

import {
  LikeOutlined,
  UserSwitchOutlined,
  DownloadOutlined,
  UserOutlined,
  AppstoreAddOutlined,
  MessageOutlined,
  StopOutlined
} from "@ant-design/icons";
import {Badge, message} from "antd";
import PageLayout from "../_partials/PageLayout";
import IconText from "../../../componets/IconText";
import StatsIcons from "../../../componets/StatsIcons";
import SummaryShow from "../../../componets/SummaryShow";
import Details from "./_partials/_Details";
import Tabs from "../../../componets/Tabs";
import Edit from "./Edit";
import Block from "./Block";
import { Button, ButtonDestroy, ButtonEdit } from "../../../componets/Buttons";
import Title from "./_partials/_Title";
import Comments from "../Users/_partials/_Comments";
import SentComments from "./_partials/_SentComments";
import EditComment from "../Comments/Edit";
import EditAddon from "../Addons/Edit";
import SidebarShow from "../Users/_partials/_SidebarShow";
import Accounts from "./_partials/_Accounts";
import usePermissions from "../../../hooks/usePermissions";

const Show: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => id ? selectUserById(state, id) : undefined);
  const { write_minecraft } = usePermissions();

  useEffect(() => {
    if (id) {
      dispatch(fetchUser(id));
    }
  }, [id, dispatch]);

  function onClickBan() {
    if (user) {
      dispatch(setBanId(user.id));
    }
  }

  function onClickEdit() {
    if (user) {
      dispatch(setEditId(user.id));
    }
  }

  function onClickDestroy() {
    if (user) {
      handleDestroy(user.id).catch(console.error);
    }
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyUser(id));

    if (destroyUser.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(minecraftUrls.skinsPath()), 1000);
    }

    if (destroyUser.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  const renderTitle = () => (
    <Badge dot color={user?.is_online ? 'green' : 'red'} offset={[5, 0]}>
      {user && <Title name={user.name} bannedUntil={user.banned_until} mutedUntil={user.muted_until} />}
    </Badge>
  )

  return (
    <PageLayout className='v-users-show'>
      <div className='v-container-fluid'>
        <div className='o-header'>
          {
            user &&
              <SummaryShow
                title={renderTitle()}
                subtitle={`Last seen: ${formattedDate(user.current_sign_in_at).fromNow}`}
                image={user.avatar_url}
                createdAt={user.created_at}
                updatedAt={user.updated_at}
                actions={write_minecraft ? [
                  <Button key='button-ban' onClick={onClickBan} title='Block' icon={StopOutlined} />,
                  <ButtonEdit key='button-edit' onClick={onClickEdit} />,
                  <ButtonDestroy key='button-destroy' onClick={onClickDestroy} />
                ] : []}
                className='v-mb-15'
              />
          }

          <StatsIcons
            icons={[
              <IconText tooltip='likes' icon={LikeOutlined} text={user?.likes_count} />,
              <IconText tooltip='downloads' icon={DownloadOutlined} text={user?.downloads_count} />,

              <IconText tooltip='addons' icon={AppstoreAddOutlined} text={user?.addons_count} />,
              <IconText tooltip='comments' icon={MessageOutlined} text={user?.comments_count} />,

              <IconText tooltip='following' icon={UserOutlined} text={user?.following_counter} />,
              <IconText tooltip='followers' icon={UserSwitchOutlined} text={user?.followers_counter} />
            ]}
          />

          <Tabs
            paramsKey='tab'
            destroyInactiveTabPane={true}
            items={[
              { label: 'Details', key: 'details', children: <Details user={user} /> },
              { label: 'Comments', key: 'comments', children: <Comments userId={user?.id} /> },
              { label: 'Comments Created', key: 'sent_comments', children: <SentComments userId={user?.id} /> },
              { label: 'Accounts', key: 'accounts', children: <Accounts userId={user?.id} /> },
            ]}
          />
        </div>
      </div>

      <Edit />
      <Block />

      <SidebarShow user={user} />

      <EditComment />
      <EditAddon />
    </PageLayout>
  )
}

export default Show;
